import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Trash2, Receipt } from 'lucide-react';
import * as api from '@/api/supervision.api';
import type { ProductoParaVender } from '@/api/supervision.api';
import { AVISO_DE_APROBACION, quedoEnAprobaciones } from '@/api/aprobaciones.api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Comprobante } from './Comprobante';
import { MODOS_DE_ENVIO } from './PagosYEnvio';
import type { ModoDeEnvio } from '@/api/tienda.api';
import { useToast } from '@/hooks/useToast';
import { dinero, fecha } from '@/lib/formato-tienda';

/**
 * PASAR UNA VENTA — desde el chat, por quien lo atiende.
 *
 * El cliente no se busca ni se teclea: es el del chat. La asesora dice qué se lleva y
 * cómo paga, y al pasarla la venta queda confirmada (baja la existencia) y a su nombre,
 * que es de donde sale «ventas pasadas» en Supervisión. Si no hay existencia no se
 * descuenta nada: queda en borrador y se le dice por qué.
 *
 * Los precios son los del catálogo. La rebaja se escribe aparte, para que quede a la vista.
 */

type Variante = ProductoParaVender['variants'][number];

/** Lo que hay de esa talla en el lugar del que sale la venta (vacío = el almacén). */
const hayEn = (v: Pick<Variante, 'enAlmacen' | 'porTienda' | 'stock'>, lugar: string) =>
  lugar ? Math.min(v.porTienda[lugar] ?? 0, v.stock) : v.enAlmacen;

interface Linea {
  productVariantId: string;
  nombre: string;
  talla: string;
  stock: number;
  enAlmacen: number;
  porTienda: Record<string, number>;
  cantidad: number;
  unitPrice: number;
  descuento: number;
}

export function PasarVenta({
  conversationId,
  nombreDelChat,
  sucursalDelCanal,
  onCerrar,
}: {
  conversationId: string;
  nombreDelChat: string;
  /** La tienda del canal por donde escribió: de ahí sale, salvo que se elija otra. */
  sucursalDelCanal?: string | null;
  onCerrar: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [buscar, setBuscar] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [nombre, setNombre] = useState(nombreDelChat);
  const [formaDePago, setFormaDePago] = useState('transferencia');
  const [envio, setEnvio] = useState('');
  const [direccion, setDireccion] = useState('');
  // Reunión del 12-oct: a la medida se paga la mitad y el resto al retirar.
  const [pago, setPago] = useState<'todo' | 'mitad' | 'otro' | 'nada'>('todo');
  const [anticipo, setAnticipo] = useState('');
  const [modo, setModo] = useState<ModoDeEnvio>('tienda');
  const [empresa, setEmpresa] = useState('');
  const [destino, setDestino] = useState('');
  const [costoMensajero, setCostoMensajero] = useState('');
  const [costoTransporte, setCostoTransporte] = useState('');
  const [comprobante, setComprobante] = useState<string | null>(null);
  // De qué lugar sale: la sucursal del canal; vacío = el almacén.
  const [lugar, setLugar] = useState(sucursalDelCanal ?? '');

  const productos = useQuery({
    queryKey: ['pasar-venta', 'productos', buscar],
    queryFn: () => api.productosParaVender(buscar),
    enabled: buscar.trim().length > 0,
  });
  const lugares = useQuery({
    queryKey: ['pasar-venta', 'lugares'],
    queryFn: api.lugaresParaVender,
  });
  const anteriores = useQuery({
    queryKey: ['pasar-venta', 'chat', conversationId],
    queryFn: () => api.ventasDelChat(conversationId),
  });

  const pasar = useMutation({
    mutationFn: () =>
      api.pasarVenta({
        conversationId,
        nombreCliente: nombre.trim() || null,
        lineas: lineas.map((l) => ({
          productVariantId: l.productVariantId,
          cantidad: l.cantidad,
          descuento: l.descuento || undefined,
        })),
        formaDePago,
        envio: envio ? Number(envio) : undefined,
        direccion: direccion.trim() || null,
        ubicacionId: lugar || null,
        pago:
          pago === 'todo' || pago === 'nada'
            ? pago
            : pago === 'mitad'
              ? Math.ceil(total / 2)
              : Math.max(0, Number(anticipo) || 0),
        envioModo: modo,
        envioEmpresa: modo === 'transportista' ? empresa.trim() || null : null,
        envioDestino: modo === 'tienda' ? null : destino.trim() || null,
        costoMensajero: modo === 'tienda' ? 0 : Number(costoMensajero) || 0,
        costoTransportista: modo === 'transportista' ? Number(costoTransporte) || 0 : 0,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['pasar-venta'] });
      setLineas([]);
      setBuscar('');
      if (r.confirmada) {
        toast.success(`Venta ${r.venta.numero} pasada`);
        setComprobante(r.venta.id);
      } else {
        toast.error(`Quedó en borrador: ${r.motivo ?? 'no se pudo confirmar'}`);
      }
    },
    onError: (e) => {
      if (quedoEnAprobaciones(e)) {
        toast.info(AVISO_DE_APROBACION, e.message);
        setLineas([]);
        setBuscar('');
        return;
      }
      toast.error(e instanceof Error ? e.message : 'No se pudo pasar la venta');
    },
  });

  function agregar(p: ProductoParaVender, varianteId: string) {
    const v = p.variants.find((x) => x.id === varianteId);
    if (!v || lineas.some((l) => l.productVariantId === varianteId)) return;
    setLineas((prev) => [
      ...prev,
      {
        productVariantId: v.id,
        nombre: p.name,
        talla: v.label,
        stock: v.stock,
        enAlmacen: v.enAlmacen,
        porTienda: v.porTienda,
        cantidad: 1,
        unitPrice: v.price ?? p.price,
        descuento: 0,
      },
    ]);
  }
  const cambiar = (i: number, cambio: Partial<Linea>) =>
    setLineas((p) => p.map((x, j) => (j === i ? { ...x, ...cambio } : x)));

  const total =
    lineas.reduce((a, l) => a + l.unitPrice * l.cantidad - l.descuento, 0) + (Number(envio) || 0);
  const pideDeMas = lineas.some((l) => l.cantidad > hayEn(l, lugar));
  const nombreDelLugar = (lugares.data ?? []).find((u) => (u.tipo === 'ALMACEN' ? '' : u.id) === lugar)?.nombre ?? 'el almacén';

  if (comprobante) {
    return <Comprobante ventaId={comprobante} leer={api.ventaDelChat} onCerrar={() => setComprobante(null)} />;
  }

  return (
    <Modal open onClose={onCerrar} title="Pasar venta" size="lg">
      <div className="space-y-4" data-pasar-venta>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Cliente (el de este chat)
          </label>
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre del cliente" />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Qué se lleva
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              className="pl-8"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              placeholder="Busca por código o por nombre…"
            />
          </div>
        </div>

        {buscar.trim() && productos.data && (
          <div className="max-h-44 space-y-2 overflow-y-auto rounded-lg border border-surface-border p-2">
            {productos.data.length === 0 && (
              <p className="py-2 text-center text-sm text-slate-400">Nada con «{buscar}».</p>
            )}
            {productos.data.map((p) => (
              <div key={p.id}>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {p.sku && <span className="font-mono text-slate-500">{p.sku} </span>}
                  {p.name} <span className="font-normal text-slate-500">· {dinero(p.price)}</span>
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {p.variants.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      disabled={v.stock <= 0}
                      onClick={() => agregar(p, v.id)}
                      className={
                        v.stock <= 0
                          ? 'min-h-[36px] cursor-not-allowed rounded-md border border-surface-border bg-slate-50 px-2.5 text-xs text-slate-400 dark:bg-slate-800'
                          : 'min-h-[36px] rounded-md border border-surface-border px-2.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-800'
                      }
                    >
                      {v.label}{' '}
                      {v.stock <= 0
                        ? '(agotada)'
                        : hayEn(v, lugar) > 0
                          ? `(${hayEn(v, lugar)})`
                          : `(0 en ${nombreDelLugar} · ${v.stock} en total)`}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {lineas.length === 0 ? (
          <p className="py-2 text-center text-sm text-slate-400">Busca una pieza arriba y elige la talla.</p>
        ) : (
          <div className="space-y-2">
            {lineas.map((l, i) => (
              <div key={l.productVariantId} className="flex flex-wrap items-center gap-2">
                <span className="min-w-[8rem] flex-1 text-sm">
                  {l.nombre} <span className="font-medium text-slate-500">· {l.talla}</span>
                </span>
                <div className="w-16 shrink-0">
                  <Input
                    type="number"
                    aria-label="Cantidad"
                    value={String(l.cantidad)}
                    onChange={(e) => cambiar(i, { cantidad: Math.max(1, Number(e.target.value) || 1) })}
                  />
                </div>
                <div className="w-24 shrink-0">
                  <Input
                    type="number"
                    aria-label="Rebaja de esta pieza"
                    placeholder="rebaja"
                    value={l.descuento ? String(l.descuento) : ''}
                    onChange={(e) => cambiar(i, { descuento: Math.max(0, Number(e.target.value) || 0) })}
                  />
                </div>
                <span className="w-24 shrink-0 text-right text-sm tabular-nums">
                  {dinero(l.unitPrice * l.cantidad - l.descuento)}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Quitar"
                  onClick={() => setLineas((p) => p.filter((_, j) => j !== i))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {pideDeMas && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                Hay una pieza que pide más de lo que hay en {nombreDelLugar}. La venta quedaría en borrador, sin descontar nada.
              </p>
            )}
          </div>
        )}

        <div className="grid gap-2 sm:grid-cols-3">
          {/* Con tiendas además del almacén, se dice de cuál sale; si no, sale del almacén. */}
          {(lugares.data ?? []).length > 1 && (
            <div>
              <label className="mb-1 block text-xs text-slate-500">Sale de</label>
              <Select value={lugar} onChange={(e) => setLugar(e.target.value)}>
                {lugares.data!.map((u) => (
                  <option key={u.id} value={u.tipo === 'ALMACEN' ? '' : u.id}>
                    {u.nombre}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs text-slate-500">Cuánto pagó hoy</label>
            <Select value={pago} onChange={(e) => setPago(e.target.value as typeof pago)}>
              <option value="todo">Todo</option>
              <option value="mitad">La mitad ({dinero(Math.ceil(total / 2))})</option>
              <option value="otro">Otro monto</option>
              <option value="nada">Nada todavía</option>
            </Select>
          </div>
          {pago === 'otro' && (
            <div>
              <label className="mb-1 block text-xs text-slate-500">Monto que dio</label>
              <Input type="number" value={anticipo} onChange={(e) => setAnticipo(e.target.value)} placeholder="0" />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs text-slate-500">Cómo sale</label>
            <Select value={modo} onChange={(e) => setModo(e.target.value as ModoDeEnvio)}>
              {MODOS_DE_ENVIO.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </div>
          {modo === 'transportista' && (
            <div>
              <label className="mb-1 block text-xs text-slate-500">Empresa</label>
              <Input value={empresa} onChange={(e) => setEmpresa(e.target.value)} placeholder="Caribe Tours" />
            </div>
          )}
          {modo !== 'tienda' && (
            <>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Destino</label>
                <Input value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="Santiago" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">
                  {modo === 'transportista' ? 'Mensajero hasta la empresa' : 'Costo del mensajero'}
                </label>
                <Input type="number" value={costoMensajero} onChange={(e) => setCostoMensajero(e.target.value)} placeholder="300" />
              </div>
            </>
          )}
          {modo === 'transportista' && (
            <div>
              <label className="mb-1 block text-xs text-slate-500">Lo que cobra la empresa</label>
              <Input type="number" value={costoTransporte} onChange={(e) => setCostoTransporte(e.target.value)} placeholder="250" />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs text-slate-500">Forma de pago</label>
            <Select value={formaDePago} onChange={(e) => setFormaDePago(e.target.value)}>
              <option value="transferencia">Transferencia</option>
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="contra entrega">Contra entrega</option>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Envío que se le cobra</label>
            <Input type="number" value={envio} onChange={(e) => setEnvio(e.target.value)} placeholder="0" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Dirección de entrega</label>
            <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Si se envía" />
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-surface-border pt-2">
          <span className="text-sm font-medium">Total</span>
          <span className="text-lg font-semibold tabular-nums">{dinero(total)}</span>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
          <Button onClick={() => pasar.mutate()} disabled={lineas.length === 0} loading={pasar.isPending}>
            Pasar venta
          </Button>
        </div>

        {(anteriores.data ?? []).length > 0 && (
          <div className="border-t border-surface-border pt-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Ventas de este chat
            </p>
            <ul className="mt-1 divide-y divide-surface-border">
              {anteriores.data!.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                  <span className="font-mono">{v.numero}</span>
                  <span className="text-slate-500">{fecha(v.createdAt)}</span>
                  <Badge
                    variant={
                      v.estado === 'CONFIRMADA' ? 'success' : v.estado === 'ANULADA' ? 'danger' : 'warning'
                    }
                  >
                    {v.estado === 'CONFIRMADA' ? 'Confirmada' : v.estado === 'ANULADA' ? 'Anulada' : 'Borrador'}
                  </Badge>
                  <span className="tabular-nums">{dinero(v.total)}</span>
                  <Button variant="outline" size="sm" onClick={() => setComprobante(v.id)}>
                    <Receipt className="h-3.5 w-3.5" /> Comprobante
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
